import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Calendar, Search, Filter, LayoutGrid, LayoutList, Download } from 'lucide-react';
import { ROOM_STATUSES } from './PmsStatusBadge';

/**
 * PmsToolbar - Sticky toolbar with filters, search, and density controls
 * Used across all PMS modules for consistent filtering UX
 */

export const PmsToolbar = ({
  onSearchChange,
  onStatusFilterChange,
  onDateChange,
  onViewModeChange,
  onDensityChange,
  showViewMode = false,
  showDensity = false,
  showDatePicker = false,
  showStatusFilter = false,
  showExport = false,
  onExport,
  defaultViewMode = 'grid',
  defaultDensity = 'comfortable',
  searchPlaceholder = 'Search...',
  className = ''
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [density, setDensity] = useState(defaultDensity);
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearchChange?.(value);
  };
  
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    onStatusFilterChange?.(value === 'all' ? null : value);
  };
  
  const handleDateChange = (e) => {
    const value = e.target.value;
    setSelectedDate(value);
    onDateChange?.(value);
  };
  
  const handleViewModeChange = (value) => {
    if (value) {
      setViewMode(value);
      onViewModeChange?.(value);
    }
  };
  
  const handleDensityChange = (value) => {
    if (value) {
      setDensity(value);
      onDensityChange?.(value);
    }
  };
  
  return (
    <div className={`sticky top-0 z-10 bg-card border-b border-border p-4 space-y-3 ${className}`} data-testid="pms-toolbar">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={handleSearchChange}
            className="pl-10"
            data-testid="pms-toolbar-search"
          />
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Date Picker */}
          {showDatePicker && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="w-auto"
                data-testid="pms-toolbar-date"
              />
            </div>
          )}
          
          {/* Export */}
          {showExport && onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              data-testid="pms-toolbar-export"
            >
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status Filter */}
          {showStatusFilter && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[150px]" data-testid="pms-toolbar-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {ROOM_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        
        {/* View Controls */}
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          {showViewMode && (
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={handleViewModeChange}
              data-testid="pms-toolbar-view-mode"
            >
              <ToggleGroupItem value="grid" aria-label="Grid view">
                <LayoutGrid className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="List view">
                <LayoutList className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          
          {/* Density Toggle */}
          {showDensity && (
            <ToggleGroup
              type="single"
              value={density}
              onValueChange={handleDensityChange}
              data-testid="pms-toolbar-density"
            >
              <ToggleGroupItem value="compact" aria-label="Compact">
                <span className="text-xs font-medium">Compact</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="comfortable" aria-label="Comfortable">
                <span className="text-xs font-medium">Comfortable</span>
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>
      </div>
    </div>
  );
};
